import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Snackbar,
  useTheme
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  AccountBalance as BalanceIcon,
  Extension as ExtensionIcon
} from '@mui/icons-material';
import api, { apiRequest } from '../utils/api';

const Maintenance = () => {
  const theme = useTheme();
  // ─── Earnings update state ──────────────────────────────────────────────
  const [earnStartDate, setEarnStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [earnDays, setEarnDays] = useState(30);
  const [earnLoading, setEarnLoading] = useState(false);
  const [earnLogs, setEarnLogs] = useState([]);
  const [earnError, setEarnError] = useState(null);

  // ─── Balance snapshot state ─────────────────────────────────────────────
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [snapshotMsg, setSnapshotMsg] = useState(null);

  // ─── Manual sync state (removed — use Sync Logs section below) ────────

  // ─── Earnings update handler ────────────────────────────────────────────
  const addEarnLog = (message) => {
    const timestamp = new Date().toLocaleTimeString();
    setEarnLogs(prev => [...prev, `[${timestamp}] ${message}`]);
  };

  const handleEarningsUpdate = async () => {
    setEarnLoading(true);
    setEarnLogs([]);
    setEarnError(null);
    try {
      addEarnLog('Starting earnings calendar update...');
      addEarnLog(`Start date: ${earnStartDate}`);
      addEarnLog(`Number of days: ${earnDays}`);

      const response = await apiRequest('/api/earnings/update', {
        method: 'POST',
        body: JSON.stringify({ startDate: earnStartDate, days: earnDays })
      });

      if (response.data?.logs && Array.isArray(response.data.logs)) {
        response.data.logs.forEach(log => addEarnLog(log));
      }
      addEarnLog(`Update completed successfully! ${response.data?.inserted || 0} records inserted.`);
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Unknown error occurred';
      setEarnError(errorMessage);
      addEarnLog(`Error: ${errorMessage}`);
    } finally {
      setEarnLoading(false);
    }
  };

  // ─── Balance snapshot handler ───────────────────────────────────────────
  const handleSnapshot = async () => {
    setSnapshotLoading(true);
    setSnapshotMsg(null);
    try {
      const response = await api.post('/api/accounts/save-daily-balances', {});
      const data = response.data;
      setSnapshotMsg({ severity: 'success', text: `Snapshot saved: ${data.savedCount} account(s), ${data.errorCount} error(s) for ${data.recordDate}` });
    } catch (error) {
      setSnapshotMsg({ severity: 'error', text: `Snapshot failed: ${error.message}` });
    } finally {
      setSnapshotLoading(false);
    }
  };

  // ─── Manual sync handler (removed — use Sync Logs section below) ──────

  return (
    <Container maxWidth="md" sx={{ mt: { xs: 1, sm: 4 }, mb: 4, px: { xs: 1, sm: 3 } }}>
      <Typography variant="body1" color="text.secondary" paragraph>
        Other maintenance operations: earnings calendar and account balance snapshots.
      </Typography>

      {/* ─── Earnings Calendar Update ─────────────────────────────────────── */}
      <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CalendarIcon color="primary" />
          <Typography variant="h5" component="h2">
            Earnings Calendar Update
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" paragraph>
          Update the earnings calendar data from the earnings API.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', mb: 3 }}>
          <Box sx={{ flex: 1 }}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={earnStartDate}
              onChange={(e) => setEarnStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={earnLoading}
              size="small"
            />
          </Box>
          <Box sx={{ width: 120 }}>
            <TextField
              fullWidth
              label="Days"
              type="number"
              value={earnDays}
              onChange={(e) => setEarnDays(Math.max(1, parseInt(e.target.value) || 1))}
              disabled={earnLoading}
              inputProps={{ min: 1 }}
              size="small"
            />
          </Box>
          <Button
            variant="contained"
            onClick={handleEarningsUpdate}
            disabled={earnLoading}
            sx={{ minWidth: 120 }}
            size="small"
          >
            {earnLoading ? <CircularProgress size={24} /> : 'Update'}
          </Button>
        </Box>

        {earnError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {earnError}
          </Alert>
        )}

        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Update Log
            </Typography>
            <Box
              sx={{
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                p: 2,
                borderRadius: 1,
                maxHeight: 300,
                overflowY: 'auto',
                fontFamily: 'monospace',
                fontSize: '0.875rem'
              }}
            >
              {earnLogs.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No logs yet. Click "Update" to start the earnings calendar update.
                </Typography>
              ) : (
                earnLogs.map((log, index) => (
                  <Box key={index} sx={{ mb: 0.5 }}>
                    {log}
                  </Box>
                ))
              )}
            </Box>
          </CardContent>
        </Card>
      </Paper>

      {/* ─── Balance Snapshot ─────────────────────────────────────────────── */}
      <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <BalanceIcon color="primary" />
          <Typography variant="h5" component="h2">
            Account Balance Snapshot
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" paragraph>
          Save current account balances (equity, buying power, cash, market value) to the daily snapshot table. Runs immediately for all active accounts.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            variant="contained"
            onClick={handleSnapshot}
            disabled={snapshotLoading}
            startIcon={snapshotLoading ? <CircularProgress size={16} /> : <BalanceIcon />}
            size="small"
          >
            {snapshotLoading ? 'Saving Snapshot...' : 'Save Balance Snapshot'}
          </Button>
        </Box>
      </Paper>

      {/* ─── Discord Alert Extension ─────────────────────────────────────── */}
      <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <ExtensionIcon color="primary" />
          <Typography variant="h5" component="h2">
            Discord Alert Extension
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" paragraph>
          Browser extension that forwards Discord channel messages to webhook alerts while
          you have a discord.com tab open. Uses your logged-in session automatically — no
          API token needed unless you set one in the extension options.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
          <Button
            variant="contained"
            component="a"
            href="/discord-alert-extension.zip"
            download
            startIcon={<ExtensionIcon />}
            size="small"
          >
            Download Extension
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" component="div">
          Install once:
          <ol style={{ margin: '4px 0 0', paddingLeft: 20 }}>
            <li>Unzip the downloaded file</li>
            <li>Open <code>chrome://extensions</code> and toggle <b>Developer mode</b></li>
            <li>Click <b>Load unpacked</b> and select the unzipped folder</li>
            <li>Keep a discord.com tab open on the alerts channel</li>
          </ol>
        </Typography>
      </Paper>

      {/* ─── Shared Snackbar ──────────────────────────────────────────────── */}
      <Snackbar
        open={!!snapshotMsg}
        autoHideDuration={6000}
        onClose={() => { setSnapshotMsg(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => { setSnapshotMsg(null); }}
          severity={(snapshotMsg)?.severity || 'info'}
          sx={{ width: '100%' }}
        >
          {(snapshotMsg)?.text}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Maintenance;
